import bindAllOfThis from "src/utils/BindThisHelper";
import { PhaserButton, PhaserButtonConfig } from './Button';
import { Modal } from 'antd';
import { Color } from "src/games/shared/ColorValue";
import { ModalOptionBase, PhaserModalBase, PhaserModalConfigBase } from "../../components/common/PhaserModalBase";

export class ModalTitleOption extends ModalOptionBase {
}

export class PhaserModalTitleConfig extends PhaserModalConfigBase {
    public title?: string;
    public bodyText?: string;
    public borderColor: number;
    public buttonWidth: number = 200;
    public buttonConfig: PhaserButtonConfig = new PhaserButtonConfig();
}

export class PhaserModalTitle extends PhaserModalBase {
    protected background: Phaser.GameObjects.Image;
    protected border: Phaser.GameObjects.Image;
    protected title: Phaser.GameObjects.Text;
    protected bodyText: Phaser.GameObjects.Text;
    protected columns: boolean = false;
    protected buttonHeight: number;
    protected padding: number = 20;
    config: PhaserModalTitleConfig;
    buttons: PhaserButton[];

    constructor(scene: Phaser.Scene, x: number, y: number, config?: PhaserModalTitleConfig, options?: ModalTitleOption[]) {
        super(scene, x, y, config);
        this.options = options;
        this.config = config;

        if (config == null) {
            this.config = new PhaserModalTitleConfig();
        }

        this.buttonHeight = 30;
        this.config.buttonConfig.backgroundColor = Color.KUDER_PRIMARY_BUTTON;
        bindAllOfThis(this, PhaserModalTitle.prototype);
        this.initialize();
        this.setScrollFactor(0);
    }

    static preload(scene: Phaser.Scene) {
        scene.load.image("pixel", require('/src/assets/qci/sprites/pixel.png'));
    }

    initialize(): void {
        var height;
        var width;

        if (this.config.title != null && this.config.bodyText != null) {
            this.createTitle();
            this.createBodyText();
            height = this.bodyText.getBottomLeft().y + (this.buttonHeight + this.padding) * (this.options.length + 1);
            width = 800;
        }
        else {
            height = this.buttonHeight * (this.options.length + 2);
            width = this.config.buttonWidth * 2;
        }

        this.setSize(width, height);

        // Construct Objects
        this.constructObjects();

        this.type = "PhaserModal";

        // Add this modal to the scene
        this.scene.add.existing(this);
    }

    /**
     * Adjusts the modal's size based on the sizes of the title and buttons within
     * Also adjusts to two columns if needed.
     */
    adjustModalSizeBasedOnItems(): void {
        var height;
        var width;
        // Set height and width to be sufficient to hold all options (assuming one column)
        if (this.config.title != null) {
            var optionSpace;
            if (this.options.length === 1) {
                optionSpace = Math.min(this.title.height, this.buttonHeight * 3) + this.padding / 2;
            }
            else {
                optionSpace = (this.buttonHeight + this.padding) * (this.options.length + 1);
            }
            height = this.bodyText.getBottomLeft().y + optionSpace + 2 * this.padding;
            width = Math.max(this.config.buttonWidth * 3, this.bodyText.width);
        }
        else {
            height = (this.buttonHeight + this.padding) * (this.options.length + 2);
            width = this.config.buttonWidth * 3;
        }

        this.setSize(width, height);

        // Now reset background and title positions and sizes
        this.border.setDisplaySize(this.width, this.height);
        this.background.setDisplaySize(this.width - this.padding, this.height - this.padding);
        if (this.config.title != null) {
            this.title.setWordWrapWidth(width - 2 * this.padding, true);
            this.title.setX(width / 2 - this.title.width / 2);
            this.bodyText.setWordWrapWidth(width - 2 * this.padding, true);
        }
    }

    /**
     * Create the buttons and at the same time keep track of the maximum buttonHeight
    */
    createButtons(): Array<PhaserButton> {
        var buttons = [];
        this.options.forEach((op: ModalTitleOption) => {
            var button = new PhaserButton(this.scene, 0, 0, this.config.buttonWidth, this.buttonHeight, op.displayText, this.config.buttonConfig);
            if (button.getIdealButtonHeight() >= this.buttonHeight) {
                this.buttonHeight = button.getIdealButtonHeight();
            }
            button.on('buttonClicked', (pointer: Phaser.Input.Pointer, x: number, y: number) => {
                if(this.scene.sound != null) {
                    this.scene.sound.stopAll();
                }
                this.emit('optionClicked', op, button);
            });
            buttons.push(button);
        });
        return buttons;
    }

    /**
    * Go through already created buttons and adjust their sizes and positions (because now we will use the correct buttonHeight)
    */
    adjustButtons(): void {
        var y;
        if (this.config.title != null && this.config.bodyText != null) {
            y = this.bodyText.getBottomLeft().y;
        }
        else if (this.config.title != null) {
            y = this.title.getBottomLeft().y;
        }
        else if (this.config.bodyText != null) {
            y = this.bodyText.getBottomLeft().y;
        }
        else {
            y = this.buttonHeight + this.padding;
        }

        var buttonIndex = 0;
        this.buttons.forEach((button: PhaserButton) => {
            var buttonX = this.getXCoordinate(buttonIndex, button);

            // Restart y value for second column
            if (this.columns && buttonIndex === Math.ceil(this.options.length / 2)) {
                if (this.config.title != null) {
                    y = this.title.getBottomLeft().y;
                }
                else {
                    y = this.buttonHeight + this.padding;
                }
            }

            // Adjust position
            button.setPosition(buttonX, y);
            button.setDisplaySize(this.config.buttonWidth, this.buttonHeight);
            button.height = this.buttonHeight;

            this.add(button);

            y += this.buttonHeight + this.padding;
            buttonIndex++;
        });
    }

    /**
    *  Helper method to get the x coordinate for the button based on if there are columns or not
    */
    getXCoordinate(buttonIndex: number, button: PhaserButton): number {
        var buttonX;
        // First column
        if (this.columns && buttonIndex < Math.ceil(this.options.length / 2)) {
            buttonX = this.width / 4 - this.config.buttonWidth / 4;
        }
        // Second column
        else if (this.columns && buttonIndex >= Math.ceil(this.options.length / 2)) {
            buttonX = this.config.buttonWidth + this.padding + this.width / 4 - button.width / 4;
        }
        // No columns (in the middle)
        else {
            buttonX = this.width / 2 - this.config.buttonWidth / 2;
        }

        return buttonX;
    }

    constructObjects(): void {
        this.border = this.scene.add.image(-this.padding / 2, -this.padding / 2, "pixel");
        this.border.setOrigin(0);
        this.border.setTint(this.config.borderColor);
        this.border.setDisplaySize(this.width, this.height);
        this.add(this.border);

        // Background of the modal
        this.background = this.scene.add.image(0, 0, "pixel");
        this.background.setOrigin(0);
        this.background.setDisplaySize(this.width - this.padding, this.height - this.padding);
        this.background.setTint(0x000000);

        this.add(this.background);

        // Size has changed so adjust text positioning
        if (this.config.title != null) {
            this.title.setWordWrapWidth(this.width - 2 * this.padding, true);
            this.title.setX((this.width - this.padding / 2) / 2 - this.title.width / 2);
            this.add(this.title);
        }
        if (this.config.bodyText != null) {
            this.bodyText.setWordWrapWidth(this.width - 2 * this.padding, true);
            this.bodyText.setX(0);
            this.add(this.bodyText);
        }

        this.buttons = this.createButtons();
        // Reset the modal size because buttonHeight was adjusted
        this.adjustModalSizeBasedOnItems();
        // Reset button positions and sizes
        this.adjustButtons();

    }

    createTitle(): void {
        this.title = this.scene.add.text(0, 0, this.config.title);
        this.title.setOrigin(0, 0);
        this.title.setTint(0xffffff);
        this.title.setFontSize(24);
        this.title.setFontFamily('Russo One, Roboto Mono, Arial');
        this.title.setPadding(15, 15, 15, 15);
        this.title.setAlign('center');
        this.title.setWordWrapWidth(this.width, true);
        this.title.setX(this.width / 2 - this.title.width / 2);
    }

    createBodyText(): void {
        this.bodyText = this.scene.add.text(0, 0, this.config.bodyText);
        this.bodyText.setOrigin(0, 0);
        this.bodyText.setTint(0xffffff);
        this.bodyText.setFontSize(20);
        this.bodyText.setFontFamily('Russo One, Roboto Mono, Arial');
        this.bodyText.setPadding(20, 20, 20, 20);
        this.bodyText.setAlign('center');
        this.bodyText.setWordWrapWidth(this.width, true);
        this.bodyText.setX(this.width / 2);
        this.bodyText.setY(this.title.getBottomLeft().y + this.padding / 2);
    }
}



// WEBPACK FOOTER //
// ./src/games/shared/ModalTitle.ts